<template>
  <loading-spinner
    v-if="isLoading"
    text-align="center"
  />

  <div
    v-if="isLoaded"
  >
    <div
      class="row"
    >
      <div
        :class="widthClass"
      >
        <dropdown
          v-if="!readonly"
          label="Gebruiker toevoegen"
          :options="dropdownOptions"
          @update:value="onDropdownValueUpdated"
        />
      </div>
    </div>
    <div 
      class="row"
    >
      <div
        v-for="user in displayedUsers" 
        :key="user.id" 
        class="my-3"
        :class="userWidth"
      >
        <user-card
          :user="user"
          @deleted="onUserDeleted"
        />
      </div>
    </div>
    <list-cutoff
      v-if="displayedUserCount < filteredUsers.length"
      :show-all-button-text="`Toon alle groepen (${filteredUsers.length})`"
      @show-all-button-pressed="onShowAllButtonPressed"
    />
  </div>
</template>

<script>
import { getAll as getAllUsers } from '@/api/providers/users';

import Dropdown from '@/components/UI/Dropdown.vue';
import UserCard from './UserCard.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import ListCutoff from '@/components/UI/ListCutoff.vue';

const State = {
  "loading": 0,
  "loaded": 1,
  "error": 2,
};

export default {
  components: {
    Dropdown,
    UserCard,
    LoadingSpinner,
    ListCutoff,
  },

  props: {
    userIds: { type: [Array, null], default: null, },
    initialDisplayedUserCount: { type: Number, default: 6, },
    fullWidth: { type: Boolean, default: false, },
    addUserCallback: { type: Function, required: true },
    removeUserCallback: { type: Function, required: true },
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      apiUsers: null,
      userList: null,
      state: State.loading,
      displayeduserCount: this.initialDisplayedUserCount,
    }
  },

  computed: {
    isLoading() {
      return this.state == State.loading;
    },
    isLoaded() {
      return this.state == State.loaded;
    },
    isError() {
      return this.state == State.error;
    },
    filteredUsers() {
      return (this.userList ?? [])
        .filter(g => !g.isDeleted);
    },
    displayedUsers() {
      return this.filteredUsers
        .slice(0, this.displayedUserCount);
    },
    addedUserIds() {
      return (this.userList ?? [])
        .filter(g => g.isNew)
        .map(g => g.id)
    },
    removedUserIds() {
      return (this.userList ?? [])
        .filter(g => g.isDeleted)
        .map(g => g.id)
    },
    widthClass() { 
      return this.fullWidth
        ? 'col-12'
        : 'col-4';
    },
    userWidth() {
      return this.fullWidth 
        ? 'col-sm-12'
        : 'col-sm-4';
    },
    dropdownOptions() {
      return (this.apiUsers ?? []).map(
        u => {
          return {
            value: u.id,
            label: `${u.firstName ?? ''} ${u.insertion ?? ''} ${u.lastName ?? ''}`,
          }
        }
      )
    },
  },

  watch: {
    userIds(v) {
      if (v == null) return;
      if (this.apiUsers == null) return;

      this.initializeUserDisplay();
    },

    apiUsers(v) {
      if (v == null) return;
      if (this.userIds == null) return;

      this.initializeUserDisplay();
    }
  },

  mounted() {
    this.loadApiUsers()
  },

  methods: {
    loadApiUsers() {
      getAllUsers()
      .then(r => this.apiUsers = r)
      .catch(() => {
        this.apiUsers = [];
        this.state = State.error
      });
    },

    initializeUserDisplay() {
      this.userList = this.apiUsers.filter(
        u => this.userIds.includes(u.id)
      );
      
      this.state = State.loaded;
    },

    onDropdownValueUpdated(id) {
      if (this.userList.find(u => u.id === id) != null) return;

      let option = this.dropdownOptions.find(o => o.value === id);
      if (option == null) return;

      let user = this.apiUsers.find(u => u.id === id);
      if (user == null) return;

      this.userList.push({
        ...user,
        isNew: true
      });
    },

    async submit(entityId) {
      await Promise.all([
        this.submitAddedUsers(entityId),
        this.submitRemovedUsers(entityId)
      ]);
    },

    async submitAddedUsers(entityId) {
      const userIds = (this.userList ?? [])
          .filter(g => g.isNew && !g.isDeleted)
          .map(g => g.id);

      if (userIds.length < 1) return;

      await this.addUserCallback(entityId, userIds);
    },

    async submitRemovedUsers(entityId) {
      let userIds = (this.userList ?? [])
          .filter(g => g.isDeleted && !g.isNew)
          .map(g => g.id);

      if (userIds.length < 1) return;
      await this.removeUserCallback(
        entityId,
        userIds
      );
    },

    onShowAllButtonPressed() {
      this.displayeduserCount = Number.MAX_SAFE_INTEGER;
    },

    onUserDeleted(id) {
      let index = this.userList.findIndex(g => g.id === id);

      if (index < 0) return;

      this.userList[index].isDeleted = true;
    }
  },
}
</script>